import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { trainingApi, type TrainingProgress } from '@/lib/apiClient';
import { TRAINING } from './content';
import type { Topic } from './content/types';
import type { TrainingViewer } from './lib/access';
import { guidedScreens, guidedTopicIds, indexOfTopic, resumeIndex, type GuidedScreen } from './lib/guided';
import { mergeProgressRow } from './lib/progress';
import TakeMeThere, { takeMeThereCss } from './TakeMeThere';

/**
 * The guide, one screen at a time.
 *
 * ⚠️ ADVANCING TICKS THE TOPIC OFF, VISIBLY. The product owner's call (2026-08-03): "yes, but
 * show it happening and allow un-ticking". A walkthrough that made you click a
 * checkbox on every screen would be homework, not a walkthrough — but a record
 * built silently would be a record nobody agreed to. So Next marks the topic
 * done, the tick is on screen, and it can be cleared without leaving the screen.
 *
 * ⚠️ Deep links and search still belong to the scrollable page. Someone who
 * followed a link to ONE topic wants that topic, not to be dropped mid-
 * walkthrough — TrainingPage keeps that job. (The `?` Help drawer links to
 * /training with no topic today; `?topic=` and `?guided=` are for links people
 * share by hand.)
 */

const css = `
  .gm-wrap { max-width: 780px; margin: 0 auto; padding: var(--ew-s-5) var(--ew-s-6) var(--ew-s-8); }
  .gm-top { display: flex; align-items: center; gap: 12px; margin-bottom: var(--ew-s-4); }
  .gm-part { font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: .08em;
             text-transform: uppercase; color: var(--ew-n-500); }
  .gm-exit { margin-left: auto; border: none; background: none; cursor: pointer; padding: 0;
             font: var(--ew-fw-medium) 12.5px/1 var(--ew-font); color: var(--ew-quality);
             border-bottom: 1px dotted currentColor; }
  .gm-bar { height: 5px; border-radius: 999px; background: var(--ew-n-100); overflow: hidden; margin-bottom: 6px; }
  .gm-bar > i { display: block; height: 100%; background: var(--ew-green); transition: width .2s ease; }
  .gm-count { font: var(--ew-fw-regular) 11.5px/1.4 var(--ew-font); color: var(--ew-n-500);
              margin-bottom: var(--ew-s-5); }

  /* A screen, not a card in a list — so it gets room and no competing borders. */
  .gm-screen { min-height: 320px; }
  .gm-sec-eyebrow { font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: .08em;
                    text-transform: uppercase; color: var(--ew-green); margin-bottom: 10px; }
  .gm-h { font: var(--ew-fw-bold) 24px/1.25 var(--ew-font); color: var(--ew-dark); margin: 0 0 10px; }
  .gm-lede { font: var(--ew-fw-regular) 15px/1.6 var(--ew-font); color: var(--ew-n-700); margin: 0 0 18px; }
  .gm-owner { display: inline-block; font: var(--ew-fw-semibold) 10px/1.6 var(--ew-font); letter-spacing: .04em;
              text-transform: uppercase; background: var(--ew-n-50); border: 1px solid var(--ew-n-200);
              color: var(--ew-n-600); border-radius: var(--ew-r-pill); padding: 1px 9px; margin-left: 8px;
              vertical-align: middle; }
  .gm-steps { margin: 0 0 16px; padding-left: 22px; }
  .gm-steps li { font: var(--ew-fw-regular) 14.5px/1.75 var(--ew-font); color: var(--ew-n-700); margin-bottom: 4px; }
  .gm-watch { background: #FFFDF5; border: 1px solid #E7D9A6; border-radius: var(--ew-r-md);
              padding: 12px 14px; margin: 0 0 16px; }
  .gm-watch b { display: block; font: var(--ew-fw-semibold) 11px/1 var(--ew-font); letter-spacing: .06em;
                text-transform: uppercase; color: #6B5D1F; margin-bottom: 6px; }
  .gm-watch ul { margin: 0; padding-left: 18px; }
  .gm-watch li { font: var(--ew-fw-regular) 13.5px/1.65 var(--ew-font); color: #6B5D1F; }
  .gm-tbl { width: 100%; border-collapse: collapse; font-size: 13px; margin: 0 0 16px; }
  .gm-tbl caption { text-align: left; font: var(--ew-fw-semibold) 11px/1.6 var(--ew-font); letter-spacing: .05em;
                    text-transform: uppercase; color: var(--ew-n-500); padding-bottom: 5px; }
  .gm-tbl th { text-align: left; padding: 8px 10px; background: var(--ew-n-50); color: var(--ew-n-600);
               font: var(--ew-fw-semibold) 11px/1.4 var(--ew-font); border-bottom: 1px solid var(--ew-n-200); }
  .gm-tbl td { padding: 8px 10px; border-bottom: 1px solid #F2F2F1; color: var(--ew-dark);
               vertical-align: top; line-height: 1.55; }
  .gm-go { font: var(--ew-fw-semibold) 13px/1 var(--ew-font); color: var(--ew-quality); text-decoration: underline; }
  .gm-where { font: var(--ew-fw-regular) 13px/1.5 var(--ew-font); color: var(--ew-n-500); }

  .gm-nav { display: flex; align-items: center; gap: 10px; margin-top: var(--ew-s-6);
            border-top: 1px solid var(--ew-n-100); padding-top: var(--ew-s-4); }
  .gm-btn { font: var(--ew-fw-semibold) 13px/1 var(--ew-font); border-radius: var(--ew-r-md); padding: 10px 18px;
            cursor: pointer; border: 1px solid var(--ew-n-300); background: #fff; color: var(--ew-dark); }
  .gm-btn.primary { background: var(--ew-green); border-color: var(--ew-green); color: #fff; }
  .gm-btn:disabled { opacity: .45; cursor: not-allowed; }
  .gm-ticked { margin-left: auto; display: inline-flex; align-items: center; gap: 6px;
               font: var(--ew-fw-medium) 12px/1 var(--ew-font); color: var(--ew-green-700); }
  .gm-ticked button { border: none; background: none; padding: 0; cursor: pointer;
                      font: var(--ew-fw-regular) 11.5px/1 var(--ew-font); color: var(--ew-n-500);
                      border-bottom: 1px dotted currentColor; }
  .gm-done { text-align: center; padding: 30px 0; }
${takeMeThereCss}
`;

/** **bold** only, same as the page. */
function Rich({ text }: { text: string }) {
  return (
    <>
      {text.split(/(\*\*[^*]+\*\*)/g).filter(Boolean).map((p, i) =>
        p.startsWith('**') && p.endsWith('**')
          ? <strong key={i}>{p.slice(2, -2)}</strong>
          : <span key={i}>{p}</span>)}
    </>
  );
}

function TopicScreen({ topic }: { topic: Topic }) {
  return (
    <>
      <h2 className="gm-h">
        {topic.title}
        {topic.owner && <span className="gm-owner" title="Who owns this work at E&amp;W">Owned by {topic.owner}</span>}
      </h2>
      <p className="gm-lede"><Rich text={topic.summary} /></p>

      {topic.steps && topic.steps.length > 0 && (
        <ol className="gm-steps">{topic.steps.map((s, i) => <li key={i}><Rich text={s} /></li>)}</ol>
      )}

      {topic.watchOut && topic.watchOut.length > 0 && (
        <div className="gm-watch">
          <b>Watch out</b>
          <ul>{topic.watchOut.map((w, i) => <li key={i}><Rich text={w} /></li>)}</ul>
        </div>
      )}

      {topic.table && (
        <table className="gm-tbl">
          {topic.table.caption && <caption>{topic.table.caption}</caption>}
          <thead><tr>{topic.table.headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
          <tbody>
            {topic.table.rows.map((r, i) => (
              <tr key={i}>{r.map((c, j) => <td key={j}><Rich text={c} /></td>)}</tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Opens beside the walkthrough rather than replacing it — the slide you
          are on is still on screen while you do the thing it describes. */}
      {topic.deepLink && <TakeMeThere href={topic.deepLink} linkClass="gm-go" />}
      {!topic.deepLink && topic.deepLinkNote && (
        <div className="gm-where">Where to find it: {topic.deepLinkNote}</div>
      )}
    </>
  );
}

export default function GuidedMode({
  viewer, trackId, progressBy, startAt, onExit, onChangeRole,
}: {
  viewer: TrainingViewer;
  trackId: string | undefined;
  progressBy: Map<string, TrainingProgress>;
  /** Topic id to open on, from a `?guided=` link. Overrides resume-by-progress. */
  startAt?: string | null;
  onExit: () => void;
  onChangeRole: () => void;
}) {
  const qc = useQueryClient();
  const screens = useMemo(() => guidedScreens(viewer, trackId), [viewer, trackId]);
  const topicIds = useMemo(() => guidedTopicIds(screens), [screens]);

  const isDone = (id: string) => progressBy.get(id)?.completedAt != null;
  /** An explicit return target wins over resume-by-progress; otherwise pick up
   *  at the first unread topic. */
  const openAt = () => {
    const at = indexOfTopic(screens, startAt);
    return at >= 0 ? at : resumeIndex(screens, isDone);
  };
  const [i, setI] = useState(openAt);
  // Changing track rebuilds the walkthrough, so the cursor has to come back too.
  useEffect(() => { setI(openAt()); /* eslint-disable-next-line */ }, [trackId, screens.length, startAt]);

  const save = useMutation({
    // Serialized: same scope id = one at a time, so two writes to the same topic
    // (tick, then undo) can't overlap. Shared with TopicProgress on the
    // reference page, which writes the same rows.
    scope: { id: 'training-progress' },
    mutationFn: (dto: { topicId: string; completed: boolean }) =>
      trainingApi.setProgress(dto.topicId, { completed: dto.completed, guideRevision: TRAINING.revision }),
    // Fold in only the row this request was about — see mergeProgressRow. The
    // response is a full snapshot, and clicking Next faster than the network is
    // intended, so replacing the cache wholesale can drop a later tick.
    onSuccess: (rows, dto) =>
      qc.setQueryData<TrainingProgress[]>(['training-progress'], (prev) =>
        mergeProgressRow(prev, rows, dto.topicId)),
  });

  if (!screens.length) {
    return (
      <div className="gm-wrap">
        <style>{css}</style>
        <p className="gm-lede">There is nothing to walk through for your access yet.</p>
        <button className="gm-btn" onClick={onExit}>Back to the guide</button>
      </div>
    );
  }

  const screen: GuidedScreen = screens[Math.min(i, screens.length - 1)];
  const doneCount = topicIds.filter(isDone).length;
  const pct = topicIds.length ? Math.round((doneCount / topicIds.length) * 100) : 0;

  const next = () => {
    // Ticking happens on ADVANCE, so "I read it and moved on" is the signal.
    // Never on Back, and never on a section or the closing screen.
    if (screen.kind === 'topic' && !isDone(screen.id)) {
      save.mutate({ topicId: screen.id, completed: true });
    }
    setI((n) => Math.min(n + 1, screens.length - 1));
  };

  return (
    <div className="gm-wrap">
      <style>{css}</style>

      <div className="gm-top">
        <span className="gm-part">{screen.part}</span>
        {/* Inside the walkthrough there are no track pills, so without this
            there is no way to say "wrong role" without leaving. */}
        <button className="gm-exit" style={{ marginLeft: 'auto' }} onClick={onChangeRole}>Change role</button>
        <button className="gm-exit" style={{ marginLeft: 0 }} onClick={onExit}>Switch to the full guide →</button>
      </div>

      <div className="gm-bar"><i style={{ width: `${pct}%` }} /></div>
      <div className="gm-count">
        {doneCount} of {topicIds.length} marked done · screen {Math.min(i + 1, screens.length)} of {screens.length}
      </div>

      <div className="gm-screen">
        {screen.kind === 'section' && (
          <>
            <div className="gm-sec-eyebrow">Next section</div>
            <h2 className="gm-h">{screen.title}</h2>
            {screen.intro && <p className="gm-lede"><Rich text={screen.intro} /></p>}
            <p className="gm-where">
              {screen.topicCount} {screen.topicCount === 1 ? 'topic' : 'topics'} in this section.
            </p>
          </>
        )}

        {screen.kind === 'topic' && <TopicScreen topic={screen.topic} />}

        {screen.kind === 'done' && (
          <div className="gm-done">
            <h2 className="gm-h">That's the walkthrough</h2>
            <p className="gm-lede">
              You've been through {doneCount} of {topicIds.length} topics. Everything else — the
              screen-by-screen reference, search, and the Word copy — is on the full guide.
            </p>
            <button className="gm-btn primary" onClick={onExit}>Open the full guide</button>
          </div>
        )}
      </div>

      {screen.kind !== 'done' && (
        <div className="gm-nav">
          <button className="gm-btn" onClick={() => setI((n) => Math.max(n - 1, 0))} disabled={i === 0}>
            ← Back
          </button>
          {/* Deliberately not disabled while the tick saves. Advancing is the
              reader's action; making them wait on our write would make a
              walkthrough feel slower than scrolling, which is the thing it is
              meant to fix. Each screen's tick is an independent write, so
              clicking ahead is safe. */}
          <button className="gm-btn primary" onClick={next}>
            {screen.kind === 'section' ? 'Start this section →' : 'Next →'}
          </button>

          {/* The tick is visible and reversible on the spot — the point of
              auto-ticking is momentum, not a record you can't correct. */}
          {screen.kind === 'topic' && isDone(screen.id) && (
            <span className="gm-ticked">
              ✓ Marked done
              <button onClick={() => save.mutate({ topicId: screen.id, completed: false })}>undo</button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}
